import React, { useState } from 'react';
import { Table, Button, Input, Select, Tag, Space, message, Tooltip } from 'antd';
import { EditOutlined, ReloadOutlined, ShoppingOutlined } from '@ant-design/icons';
import { PostUserEquipment } from '../../../dao/jutge/GetJavamonConfig';
import { javamonImage } from './javamonUtils';
import UserEquipmentEditModal from '../components/UserEquipmentEditModal';
import UserInventoryModal from './UserInventoryModal';

interface TabUserEquipmentProps {
    isAdmin: boolean;
    isProfesor: boolean;
    currentUserId?: string | number;
    loading: boolean;
    usersEquipment: any[];
    skinsConfig: any;
    teacherAulas: any[];
    selectedAulaIdEquipment: string | number;
    setSelectedAulaIdEquipment: (val: string | number) => void;
    aulaStudentsMap: Record<string | number, Set<number>>;
    loadStudentsForAula: (aulaId: string | number) => Promise<void>;
    fetchUsersEquipment: () => Promise<void>;
    fetchSkinsConfig: () => Promise<void>;
}

export const TabUserEquipment: React.FC<TabUserEquipmentProps> = ({
    isAdmin,
    isProfesor,
    currentUserId,
    loading,
    usersEquipment,
    skinsConfig,
    teacherAulas,
    selectedAulaIdEquipment,
    setSelectedAulaIdEquipment,
    aulaStudentsMap,
    loadStudentsForAula,
    fetchUsersEquipment,
    fetchSkinsConfig
}) => {
    const [searchUser, setSearchUser] = useState('');
    const [userEquipmentModal, setUserEquipmentModal] = useState(false);
    const [editingUser, setEditingUser] = useState<any>(null);
    const [userInventoryModal, setUserInventoryModal] = useState(false);
    const [inventoryUser, setInventoryUser] = useState<any>(null);

    const canEditEquip = isAdmin || isProfesor;

    const filteredUsersEquipment = usersEquipment.filter((r: any) => {
        const uId = r.id_usuari != null ? Number(r.id_usuari) : (r.id != null ? Number(r.id) : null);
        const userRol = Number(r.rol);

        // El usuario admin (rol 0) solo se ha de mostrar si es el admin el que está logueado
        if (userRol === 0 && !isAdmin) {
            return false;
        }

        // Filtro por aula seleccionada
        const userAulas: string[] = r.aulas ? String(r.aulas).split(',').map((s: string) => s.trim()) : [];

        if (selectedAulaIdEquipment !== 'all') {
            const selectedStr = String(selectedAulaIdEquipment);
            if (r.aulas !== undefined && r.aulas !== null) {
                if (!userAulas.includes(selectedStr)) {
                    return false;
                }
            } else {
                const studentSet = aulaStudentsMap[selectedAulaIdEquipment] || aulaStudentsMap[selectedStr];
                if (!studentSet || (uId != null && !studentSet.has(uId))) {
                    return false;
                }
            }
        } else if (isProfesor) {
            // Si es profesor y tiene 'all', solo ver los alumnos pertenecientes a alguna de sus aulas
            if (r.aulas !== undefined && r.aulas !== null) {
                const teacherAulaIds = new Set(teacherAulas.map((a: any) => String(a.id)));
                const inTeacherAula = userAulas.some((aid: string) => teacherAulaIds.has(aid));
                if (!inTeacherAula) return false;
            } else if (Object.keys(aulaStudentsMap).length > 0) {
                const inAnyAula = Object.values(aulaStudentsMap).some(set => uId != null && set.has(uId));
                if (!inAnyAula) return false;
            }
        }

        const fullName = `${r.nom || ''} ${r.cognoms || ''}`.toLowerCase();
        return fullName.includes(searchUser.toLowerCase()) || (r.email || '').toLowerCase().includes(searchUser.toLowerCase());
    });

    const handleSaveUserEquipment = async (values: any) => {
        try {
            const unlocked = values.unlocked_avatars || [];
            if (!unlocked.includes(1)) {
                unlocked.push(1);
            }
            const unlockedSkins = values.unlocked_skins || [];
            if (!unlockedSkins.includes(1)) {
                unlockedSkins.push(1);
            }
            const unlockedArmes = values.unlocked_armes || [];
            if (!unlockedArmes.includes(1)) {
                unlockedArmes.push(1);
            }
            const unlockedEscuts = values.unlocked_escuts || [];
            if (!unlockedEscuts.includes(1)) {
                unlockedEscuts.push(1);
            }
            const unlockedArcs = values.unlocked_arcs || [];
            if (!unlockedArcs.includes(1)) {
                unlockedArcs.push(1);
            }
            const unlockedFlechas = values.unlocked_flechas || [];
            if (!unlockedFlechas.includes(1)) {
                unlockedFlechas.push(1);
            }
            const unlockedMagias = values.unlocked_magias || [];
            if (!unlockedMagias.includes(1)) {
                unlockedMagias.push(1);
            }

            const body = {
                id_usuari: editingUser?.id_usuari,
                id_skin: values.id_skin || null,
                id_arma: values.id_arma || null,
                id_escut: values.id_escut || null,
                id_arc: values.id_arc || null,
                id_flecha: values.id_flecha || null,
                id_magia: values.id_magia || null,
                id_avatar: values.id_avatar || null,
                unlocked_avatars: unlocked,
                unlocked_skins: unlockedSkins,
                unlocked_armes: unlockedArmes,
                unlocked_escuts: unlockedEscuts,
                unlocked_arcs: unlockedArcs,
                unlocked_flechas: unlockedFlechas,
                unlocked_magias: unlockedMagias
            };

            const res = await PostUserEquipment(body);
            if (res) {
                message.success("Equipamiento guardado correctamente");
                setUserEquipmentModal(false);
                fetchUsersEquipment();
            } else {
                throw new Error("Error en api");
            }
        } catch (e) {
            message.error("Error al guardar equipamiento");
        }
    };

    const columnsUserEquipment = [
        {
            title: 'Usuario',
            key: 'user',
            render: (_: any, r: any) => {
                const profileImg = r.profile_avatar || null;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {profileImg ? (
                            <img
                                src={profileImg}
                                alt="avatar"
                                style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid #eaeaea' }}
                            />
                        ) : (
                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                👤
                            </div>
                        )}
                        <div>
                            <div style={{ fontWeight: 'bold' }}>{r.nom} {r.cognoms}</div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{r.email}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Equipamiento Equipado',
            key: 'equipped_items',
            align: 'center' as const,
            render: (_: any, r: any) => {
                const s = skinsConfig.skins?.find((item: any) => item.id === r.id_skin);
                const w = skinsConfig.armes?.find((item: any) => item.id === r.id_arma);
                const e = skinsConfig.escuts?.find((item: any) => item.id === r.id_escut);
                const a = skinsConfig.arcs?.find((item: any) => item.id === r.id_arc);
                const f = skinsConfig.flechas?.find((item: any) => item.id === r.id_flecha);
                const m = skinsConfig.magias?.find((item: any) => item.id === r.id_magia);

                const sImg = s ? javamonImage(s) : null;
                const wImg = w ? javamonImage(w) : null;
                const eImg = e ? javamonImage(e) : null;
                const aImg = a ? javamonImage(a) : null;
                const fImg = f ? javamonImage(f) : null;
                const mImg = m ? javamonImage(m) : null;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {/* Escudo */}
                        <Tooltip title={e ? `Escudo: ${e.nom}` : "Sin Escudo"}>
                            <div style={{ width: 38, height: 38, border: '1px dashed #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', background: '#fafafa' }}>
                                {eImg ? <img src={eImg} alt="escudo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '16px', filter: 'grayscale(1)' }}>🛡️</span>}
                            </div>
                        </Tooltip>

                        {/* Skin */}
                        <Tooltip title={s ? `Skin: ${s.nom}` : "Sin Skin"}>
                            <div style={{ width: 46, height: 46, border: '1px solid #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', background: '#f0f2f5', boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)' }}>
                                {sImg ? <img src={sImg} alt="skin" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '22px', filter: 'grayscale(1)' }}>👕</span>}
                            </div>
                        </Tooltip>

                        {/* Arma */}
                        <Tooltip title={w ? `Arma: ${w.nom}` : "Sin Arma"}>
                            <div style={{ width: 38, height: 38, border: '1px dashed #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', background: '#fafafa' }}>
                                {wImg ? <img src={wImg} alt="arma" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '16px', filter: 'grayscale(1)' }}>⚔️</span>}
                            </div>
                        </Tooltip>

                        {/* Arco */}
                        <Tooltip title={a ? `Arco: ${a.nom}` : "Sin Arco"}>
                            <div style={{ width: 38, height: 38, border: '1px dashed #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', background: '#fafafa' }}>
                                {aImg ? <img src={aImg} alt="arco" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '16px', filter: 'grayscale(1)' }}>🏹</span>}
                            </div>
                        </Tooltip>

                        {/* Flecha */}
                        <Tooltip title={f ? `Flecha: ${f.nom}` : "Sin Flechas"}>
                            <div style={{ width: 38, height: 38, border: '1px dashed #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', background: '#fafafa' }}>
                                {fImg ? <img src={fImg} alt="flecha" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '16px', filter: 'grayscale(1)' }}>🎯</span>}
                            </div>
                        </Tooltip>

                        {/* Magia */}
                        <Tooltip title={m ? `Magia: ${m.nom}` : "Sin Magia"}>
                            <div style={{ width: 38, height: 38, border: '1px dashed #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', background: '#fafafa' }}>
                                {mImg ? <img src={mImg} alt="magia" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '16px', filter: 'grayscale(1)' }}>✨</span>}
                            </div>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            title: 'Desbloqueados',
            key: 'unlocked_summary',
            render: (_: any, r: any) => {
                const countAv = r.unlocked_avatars ? r.unlocked_avatars.split(',').length : 0;
                const countSkin = r.unlocked_skins ? r.unlocked_skins.split(',').length : 0;
                const countArma = r.unlocked_armes ? r.unlocked_armes.split(',').length : 0;
                const countEscut = r.unlocked_escuts ? r.unlocked_escuts.split(',').length : 0;
                const countArc = r.unlocked_arcs ? r.unlocked_arcs.split(',').length : 0;
                const countFlecha = r.unlocked_flechas ? r.unlocked_flechas.split(',').length : 0;
                const countMagia = r.unlocked_magias ? r.unlocked_magias.split(',').length : 0;

                return (
                    <Space size={4} wrap>
                        <Tooltip title={`${countAv} Avatares`}>
                            <Tag color="purple" style={{ margin: 0 }}>👤 {countAv}</Tag>
                        </Tooltip>
                        <Tooltip title={`${countSkin} Skins`}>
                            <Tag color="magenta" style={{ margin: 0 }}>👕 {countSkin}</Tag>
                        </Tooltip>
                        <Tooltip title={`${countArma} Armas`}>
                            <Tag color="green" style={{ margin: 0 }}>⚔️ {countArma}</Tag>
                        </Tooltip>
                        <Tooltip title={`${countEscut} Escudos`}>
                            <Tag color="orange" style={{ margin: 0 }}>🛡️ {countEscut}</Tag>
                        </Tooltip>
                        <Tooltip title={`${countArc} Arcos`}>
                            <Tag color="cyan" style={{ margin: 0 }}>🏹 {countArc}</Tag>
                        </Tooltip>
                        <Tooltip title={`${countFlecha} Flechas`}>
                            <Tag color="geekblue" style={{ margin: 0 }}>🎯 {countFlecha}</Tag>
                        </Tooltip>
                        <Tooltip title={`${countMagia} Magias`}>
                            <Tag color="gold" style={{ margin: 0 }}>✨ {countMagia}</Tag>
                        </Tooltip>
                    </Space>
                );
            }
        },
        ...(canEditEquip ? [{
            title: 'Acciones',
            key: 'actions',
            render: (_: any, r: any) => {
                const targetUid = r.id_usuari != null ? Number(r.id_usuari) : (r.id != null ? Number(r.id) : null);
                const targetRol = Number(r.rol);
                const isSelf = currentUserId != null && targetUid === Number(currentUserId);

                // Regla: El resto de profesores no se han de poder editar, solo puedes editar el profesor logueado (a ti mismo).
                // Si es admin, puede editar a todos; si es profesor y el target es profesor (rol 1), solo si es él mismo.
                const canEditThisUser = isAdmin || (targetRol === 1 ? isSelf : true);

                if (!canEditThisUser) return null;

                return (
                    <Space size={6}>
                        <Tooltip title="Gestionar Inventari, Javamons i Recompenses">
                            <Button
                                icon={<ShoppingOutlined />}
                                style={{ color: '#722ed1', borderColor: '#d3adf7' }}
                                onClick={() => {
                                    setInventoryUser(r);
                                    setUserInventoryModal(true);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Editar Equipament Equipat i Desbloquejats">
                            <Button icon={<EditOutlined />} onClick={() => {
                                setEditingUser(r);
                                setUserEquipmentModal(true);
                            }} />
                        </Tooltip>
                    </Space>
                );
            }
        }] : [])
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }} wrap>
                {teacherAulas.length > 0 && (
                    <Select
                        value={selectedAulaIdEquipment}
                        onChange={(val) => {
                            setSelectedAulaIdEquipment(val);
                            loadStudentsForAula(val);
                        }}
                        style={{ width: 240 }}
                        placeholder="Seleccionar aula"
                        showSearch
                        optionFilterProp="children"
                    >
                        <Select.Option value="all">
                            🏫 {isProfesor ? `Todas mis aulas (${teacherAulas.length})` : `Todas las aulas (${teacherAulas.length})`}
                        </Select.Option>
                        {teacherAulas.map((aula: any) => (
                            <Select.Option key={aula.id} value={aula.id}>
                                🏫 {aula.nom || `Aula ${aula.id}`}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                <Input
                    placeholder="Buscar alumno por nombre o email..."
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    style={{ width: 320 }}
                    allowClear
                />
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => { fetchUsersEquipment(); fetchSkinsConfig(); }}
                    loading={loading}
                >
                    Refrescar
                </Button>
            </Space>

            <Table dataSource={filteredUsersEquipment} columns={columnsUserEquipment} loading={loading} rowKey="id_usuari" />

            {/* Modal Equipamiento de Usuario con Cards e Interfaz Visual */}
            {userEquipmentModal && editingUser && (
                <UserEquipmentEditModal
                    visible={userEquipmentModal}
                    onClose={() => {
                        setUserEquipmentModal(false);
                        fetchUsersEquipment();
                    }}
                    editingUser={editingUser}
                    skinsConfig={skinsConfig}
                    onSaved={() => fetchUsersEquipment()}
                />
            )}

            {/* Modal de Gestió d'Inventari, Javamons i Recompenses */}
            {userInventoryModal && inventoryUser && (
                <UserInventoryModal
                    visible={userInventoryModal}
                    onClose={() => {
                        setUserInventoryModal(false);
                        setInventoryUser(null);
                    }}
                    user={inventoryUser}
                    aulaId={selectedAulaIdEquipment !== 'all' ? selectedAulaIdEquipment : 0}
                />
            )}
        </div>
    );
};
